import {hasKey2} from '@app/helpers/type-guards';
import {useMemo} from 'react';

import {ColorName} from '../Theme/colors';
import {Variant} from './Btn';

const LEGACY_VARIANTS = {
  primary: {variant: 'filled', color: 'primary'},
  link: {variant: 'filled', color: 'link'},
  secondarySurface: {variant: 'filled', color: 'secondarySurface'},
  secondary: {variant: 'outline', color: 'secondary'},
  default: {variant: 'outline', color: 'secondary'},
  success: {variant: 'filled', color: 'success'},
  important: {variant: 'outline', color: 'error'},
  danger: {variant: 'filled', color: 'error'},
  warn: {variant: 'filled', color: 'warn'},
  paywall: {variant: 'filled', color: 'orange'},
  info: {variant: 'filled', color: 'primary'},
  transparent: {variant: 'text', color: 'secondary'},
  primaryOutline: {variant: 'outline', color: 'primary'},
} as const;

const DEFAULT_COLOR_PALETTE = {
  backgroundColor: 'gray-0',
  contentColor: 'gray-600',
  hoverColor: 'gray-200',
  outlineColor: 'gray-200',
};

export type LegacyVariantName = keyof typeof LEGACY_VARIANTS;

export const useLegacyVariantColors = (
  rawVariant: Variant | LegacyVariantName,
  rawColor: ColorName
) => {
  const [variant, color] = useMemo(() => {
    if (hasKey2(LEGACY_VARIANTS, rawVariant)) {
      const {variant, color} = LEGACY_VARIANTS[rawVariant];
      return [variant, color];
    } else {
      return [rawVariant, rawColor];
    }
  }, [rawVariant, rawColor]);

  return {variant, color};
};

const FILLED_COLOR_MAP = {
  primary: {
    backgroundColor: 'blue-500',
    contentColor: 'gray-0',
    hoverColor: 'blue-600',
  },
  secondary: {
    backgroundColor: 'gray-100',
    contentColor: 'gray-700',
    hoverColor: 'gray-200',
  },
  secondarySurface: {
    backgroundColor: 'gray-50',
    contentColor: 'gray-700',
    hoverColor: 'gray-200',
  },
  error: {
    backgroundColor: 'red-500',
    contentColor: 'red-50',
    hoverColor: 'red-400',
  },
  warn: {
    backgroundColor: 'yellow-100',
    contentColor: 'yellow-600',
    hoverColor: 'yellow-50',
  },
  success: {
    backgroundColor: 'green-600',
    contentColor: 'gray-0',
    hoverColor: 'green-700',
  },
  copilot: {
    backgroundColor: 'purple-500',
    contentColor: 'gray-0',
    hoverColor: 'purple-600',
  },
  link: {
    contentColor: 'blue-500',
  },
  orange: {
    backgroundColor: 'orange-500',
    contentColor: 'gray-0',
    hoverColor: 'orange-600',
  },
};

const OUTLINE_COLOR_MAP = {
  primary: {
    outlineColor: 'blue-500',
    backgroundColor: 'blue-50',
    contentColor: 'blue-600',
    hoverColor: 'blue-50',
    hoverOutlineColor: 'blue-500',
  },
  secondary: {
    outlineColor: 'gray-200',
    backgroundColor: 'gray-0',
    contentColor: 'gray-600',
    hoverColor: 'gray-0',
    hoverOutlineColor: 'gray-400',
  },
  error: {
    outlineColor: 'gray-200',
    backgroundColor: 'red-50',
    contentColor: 'red-500',
    hoverOutlineColor: 'gray-400',
  },
  warn: {
    outlineColor: 'gray-200',
    backgroundColor: 'yellow-50',
    contentColor: 'yellow-500',
    hoverOutlineColor: 'gray-400',
  },
  success: {
    outlineColor: 'gray-200',
    backgroundColor: 'gray-0',
    contentColor: 'green-500',
    hoverOutlineColor: 'gray-400',
  },
  copilot: {
    outlineColor: 'gray-200',
    backgroundColor: 'gray-0',
    contentColor: 'purple-500',
    hoverOutlineColor: 'gray-400',
  },
  link: {
    outlineColor: 'gray-200',
    backgroundColor: 'gray-0',
    contentColor: 'blue-600',
    hoverOutlineColor: 'gray-400',
  },
};

const TEXT_COLOR_MAP = {
  ...OUTLINE_COLOR_MAP,
  secondary: {
    backgroundColor: 'gray-0',
    hoverColor: 'gray-50',
    contentColor: 'gray-600',
  },
};

export interface ButtonColorPalette {
  backgroundColor?: string;
  contentColor?: string;
  hoverColor?: string;
  outlineColor?: string;
  hoverOutlineColor?: string;
}

export const useConcreteColorsFromVariant = (
  variant: Variant,
  color: ColorName
) => {
  return useMemo((): ButtonColorPalette => {
    if (['filled', 'filled_tonal'].includes(variant)) {
      return (
        FILLED_COLOR_MAP[color as keyof typeof FILLED_COLOR_MAP] ??
        DEFAULT_COLOR_PALETTE
      );
    } else if (['outline', 'text_button'].includes(variant)) {
      const colorMap: ButtonColorPalette =
        OUTLINE_COLOR_MAP[color as keyof typeof OUTLINE_COLOR_MAP] ??
        DEFAULT_COLOR_PALETTE;
      if (variant === 'outline') {
        return colorMap;
      } else {
        // Exclude outlineColor and hoverOutlineColor for non-outline variants
        return {
          backgroundColor: colorMap.backgroundColor,
          contentColor: colorMap.contentColor,
          hoverColor: colorMap.hoverColor ?? colorMap.backgroundColor,
        };
      }
    } else if (variant === 'text') {
      const colorMap: ButtonColorPalette =
        TEXT_COLOR_MAP[color as keyof typeof TEXT_COLOR_MAP] ??
        DEFAULT_COLOR_PALETTE;
      return colorMap;
    }
    return DEFAULT_COLOR_PALETTE;
  }, [variant, color]);
};
